import { animate, useInView, useReducedMotion } from "framer-motion";
import { useEffect, useMemo, useRef, useState } from "react";

export type TParsedLandingStatValue = {
	prefix: string;
	value: number;
	suffix: string;
};

/** Parses display strings like "+126%" into animatable parts. */
export function parseLandingStatValue(raw: string): TParsedLandingStatValue | null {
	const match = raw.trim().match(/^([+-]?)(\d+(?:\.\d+)?)(.*)$/);
	if (!match) return null;
	return {
		prefix: match[1],
		value: Number(match[2]),
		suffix: match[3],
	};
}

const COUNT_UP_DURATION_S = 2;
const COUNT_UP_EASE: [number, number, number, number] = [0.22, 1, 0.36, 1];

/** Counts a stat value up from zero the first time it enters the viewport. */
export function StatCountUpValue({ value, delay = 0 }: { value: string; delay?: number }) {
	const parsed = useMemo(() => parseLandingStatValue(value), [value]);
	const ref = useRef<HTMLSpanElement>(null);
	const reducedMotion = useReducedMotion();
	const isInView = useInView(ref, { once: true, amount: 0.55 });
	const [displayValue, setDisplayValue] = useState(() => (parsed ? 0 : null));

	useEffect(() => {
		if (!parsed) return;
		if (!isInView) return;

		if (reducedMotion) {
			setDisplayValue(parsed.value);
			return;
		}

		let timeoutId = 0;
		let controls: ReturnType<typeof animate> | undefined;

		const start = () => {
			setDisplayValue(0);
			controls = animate(0, parsed.value, {
				duration: COUNT_UP_DURATION_S,
				ease: COUNT_UP_EASE,
				onUpdate: (next) => setDisplayValue(Math.round(next)),
			});
		};

		if (delay > 0) {
			timeoutId = window.setTimeout(start, delay * 1000);
		} else {
			start();
		}

		return () => {
			window.clearTimeout(timeoutId);
			controls?.stop();
		};
	}, [delay, isInView, parsed, reducedMotion]);

	if (!parsed || displayValue === null) {
		return <span ref={ref}>{value}</span>;
	}

	return (
		<span ref={ref}>
			{parsed.prefix}
			{displayValue}
			{parsed.suffix}
		</span>
	);
}
